/**
 * Response Parser Utility
 * Detects and parses different response formats (JSON, Markdown, plain text)
 * and converts structured text into rich visual components
 */

import type { Tag } from '../components/results/TagChips';

export type ResponseType = 'json' | 'markdown' | 'plain';

export interface ParsedResponse {
  type: ResponseType;
  content: any;
  raw: string;
}

/**
 * Parse AI response and detect its type
 */
export function parseResponse(content: string): ParsedResponse {
  const trimmed = content.trim();

  // Try to detect and parse JSON
  if (isJsonStructured(trimmed)) {
    try {
      const parsed = JSON.parse(trimmed);
      return {
        type: 'json',
        content: parsed,
        raw: content,
      };
    } catch (e) {
      // If JSON parsing fails, continue to other checks
    }
  }

  // Try to detect **bold title** – description pattern first
  const boldTitleCards = extractBoldTitleCards(trimmed);
  if (boldTitleCards && boldTitleCards.length > 0) {
    return {
      type: 'json',
      content: { cards: boldTitleCards },
      raw: content,
    };
  }

  // Try to auto-convert structured text to JSON format
  const autoStructured = autoConvertToStructured(trimmed);
  if (autoStructured) {
    return {
      type: 'json',
      content: autoStructured,
      raw: content,
    };
  }

  // Check for Markdown formatting
  if (isMarkdownFormatted(trimmed)) {
    return {
      type: 'markdown',
      content: trimmed,
      raw: content,
    };
  }

  // Default to plain text
  return {
    type: 'plain',
    content: trimmed,
    raw: content,
  };
}

/**
 * Check if content is JSON-structured
 */
function isJsonStructured(content: string): boolean {
  // Check for JSON object or array
  const firstChar = content.charAt(0);
  const lastChar = content.charAt(content.length - 1);
  
  return (
    (firstChar === '{' && lastChar === '}') ||
    (firstChar === '[' && lastChar === ']')
  );
}

/**
 * Check if content has Markdown formatting
 */
function isMarkdownFormatted(content: string): boolean {
  // Check for common Markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/m,           // Headers
    /\*\*.*?\*\*/,          // Bold
    /\*.*?\*/,              // Italic
    /\[.*?\]\(.*?\)/,       // Links
    /^[-*+]\s/m,            // Unordered lists
    /^\d+\.\s/m,            // Ordered lists
    /^```/m,                // Code blocks
    /`[^`]+`/,              // Inline code
    /^>/m,                  // Blockquotes
  ];

  return markdownPatterns.some(pattern => pattern.test(content));
}

/**
 * Validate if parsed JSON matches expected structure
 */
export function isValidStructuredResponse(data: any): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check for common structured response patterns
  // Sections array
  if (Array.isArray(data.sections)) {
    return data.sections.every((section: any) => 
      typeof section === 'object' && section.title
    );
  }

  // Cards array
  if (Array.isArray(data.cards)) {
    return data.cards.every((card: any) => 
      typeof card === 'object' && card.title
    );
  }

  // Tips array
  if (Array.isArray(data.tips)) {
    return data.tips.every((tip: any) => 
      typeof tip === 'object' && tip.message
    );
  }

  // Single object with title/content
  if (data.title || data.content) {
    return true;
  }

  return false;
}

/**
 * Tip detection patterns - used for identifying tip lines
 */
const TIP_PATTERNS = [
  /^(tip|consejo|recomendación|importante|nota|note|important|aviso|advertencia):?\s*/i,
  /^[💡ℹ️⚠️✅❗⭐🌟]/,
  /^\*\*(tip|consejo|recomendación|importante|nota|note|important):?\*\*/i,
];

/**
 * Section title patterns - improved to detect various section headers
 */
const SECTION_TITLE_PATTERNS = [
  /^(main areas|zonas principales|principales zonas|areas principales)/i,
  /^(what to do|qué hacer|que hacer|things to do|actividades)/i,
  /^(where to stay|dónde alojarse|donde alojarse|accommodation|hospedaje)/i,
  /^(best time|mejor época|mejor temporada|when to visit|cuándo visitar)/i,
  /^(getting around|cómo moverse|como moverse|transportation|transporte)/i,
  /^(tips|consejos|recommendations|recomendaciones)/i,
  /^(food|comida|restaurants|restaurantes|dining|gastronomía)/i,
  /^(beaches|playas|coast|costa)/i,
  /^(culture|cultura|history|historia)/i,
  /^(shopping|compras|tiendas|stores)/i,
];

/**
 * Conversational patterns to filter out
 */
const CONVERSATIONAL_PATTERNS = [
  /^si me dices/i,
  /^if you tell me/i,
  /^si me das más/i,
  /^if you give me more/i,
  /^te doy opciones/i,
  /^i('ll| will) give you/i,
  /^necesito más/i,
  /^i need more/i,
  /^déjame saber/i,
  /^let me know/i,
];

/**
 * Summary detection patterns
 */
const SUMMARY_PATTERNS = [
  /^(resumen|summary|overview|about|acerca de|introducción|introduction):/i,
  /^(resumen|summary|overview|about|acerca de|introducción|introduction)$/i,
];

/**
 * Auto-convert structured text patterns into JSON format
 * Detects patterns like:
 * - Summary paragraphs
 * - Numbered lists (1. Item, 2. Item)
 * - Bullet lists (•, -, *)
 * - URLs and links
 * - Sections with titles
 * - Tips/recommendations
 * - TrustScore mentions
 * - Service/product mentions
 */
function autoConvertToStructured(text: string): any | null {
  const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
  
  if (lines.length === 0) {
    return null;
  }

  const result: any = {};
  let hasStructure = false;
  const processedIndices = new Set<number>();

  // Extract summary if present (look for first substantial paragraph or summary section)
  const summaryData = extractSummary(lines);
  if (summaryData.summary) {
    result.summary = summaryData.summary;
    summaryData.processedIndices.forEach(idx => processedIndices.add(idx));
    hasStructure = true;
  }

  // Extract TrustScore if mentioned
  const trustScore = extractTrustScore(text);
  if (trustScore !== null) {
    result.trustScore = trustScore;
    hasStructure = true;
  }

  // Extract title (first line if it's short and not a list item, and not already used as summary)
  if (lines.length > 2 && lines[0].length < 100 && !lines[0].match(/^[\d•\-*]/) && !processedIndices.has(0)) {
    // Check if second line looks like a list or section - if so, first line is likely a title
    if (lines[1].match(/^[\d•\-*]/) || lines[1].endsWith(':')) {
      result.title = lines[0];
      processedIndices.add(0);
      hasStructure = true;
    }
  }

  // Extract tips first (they're distinctive and should be detected early)
  const tips = extractTips(lines);
  if (tips.length > 0) {
    result.tips = tips;
    hasStructure = true;
    // Mark tip lines as processed by checking for tip patterns
    lines.forEach((line, i) => {
      if (!processedIndices.has(i) && TIP_PATTERNS.some(p => p.test(line))) {
        processedIndices.add(i);
      }
    });
  }

  // Try to extract cards from numbered/bullet lists
  const unprocessedLines = lines.filter((_, i) => !processedIndices.has(i));
  const cards = extractCards(unprocessedLines);
  if (cards.length > 0) {
    result.cards = cards;
    hasStructure = true;
    // Mark card-related lines as processed by looking for list patterns
    const numberedPattern = /^(\d+)\.\s*/;
    const bulletPattern = /^[•\-*]\s+/;
    
    cards.forEach(card => {
      // Find line that starts with number/bullet and contains card title
      const titleStart = card.title.substring(0, Math.min(30, card.title.length));
      const idx = lines.findIndex((l, i) => {
        if (processedIndices.has(i)) return false;
        return (numberedPattern.test(l) || bulletPattern.test(l)) && l.includes(titleStart);
      });
      
      if (idx >= 0) {
        processedIndices.add(idx);
        // Check if next line is description (starts with dash/bullet or indented)
        if (idx + 1 < lines.length && !processedIndices.has(idx + 1)) {
          const nextLine = lines[idx + 1];
          if (card.description && (nextLine.match(/^[-•*]\s/) || nextLine.match(/^\s{2,}/))) {
            processedIndices.add(idx + 1);
          }
        }
      }
    });
  }

  // Try to extract sections from remaining lines
  const unprocessedForSections = lines.filter((_, i) => !processedIndices.has(i));
  const sections = extractSections(unprocessedForSections);
  if (sections.length > 0) {
    result.sections = sections;
    hasStructure = true;
    // Mark section lines as processed
    sections.forEach(section => {
      const titleIdx = lines.findIndex((l, i) => !processedIndices.has(i) && (l === section.title || l === section.title + ':'));
      if (titleIdx >= 0) {
        processedIndices.add(titleIdx);
        // Mark content lines as processed (approximately)
        const contentLines = section.content.split('\n').length;
        for (let j = 1; j <= contentLines && titleIdx + j < lines.length; j++) {
          processedIndices.add(titleIdx + j);
        }
      }
    });
  }

  // Add remaining content as description if not too long
  const remainingLines = lines.filter((_, i) => !processedIndices.has(i));
  const remainingContent = remainingLines.join('\n').trim();
  
  if (remainingContent) {
    if (remainingContent.length < 500 && !result.summary) {
      // Only use as description if we don't already have a summary
      result.description = remainingContent;
      hasStructure = true;
    } else if (!result.sections && !result.cards) {
      // If we haven't found much structure and content is long, don't force structure
      return null;
    }
  }

  return hasStructure ? result : null;
}

/**
 * Extract summary from the beginning of the content
 * Looks for explicit summary sections or first substantial paragraph
 */
function extractSummary(lines: string[]): { summary: string | null; processedIndices: number[] } {
  const processedIndices: number[] = [];
  
  // Look for explicit summary section
  for (let i = 0; i < lines.length && i < 5; i++) {
    const line = lines[i];
    const isSummaryHeader = SUMMARY_PATTERNS.some(pattern => pattern.test(line));
    
    if (isSummaryHeader) {
      // Next lines until empty line or another section are the summary
      const summaryLines: string[] = [];
      for (let j = i + 1; j < lines.length; j++) {
        const nextLine = lines[j];
        // Stop at section headers or empty equivalent
        if (nextLine.endsWith(':') && nextLine.length < 50) break;
        if (nextLine.match(/^#{1,6}\s/)) break;
        if (nextLine.match(/^\d+\./)) break;
        
        summaryLines.push(nextLine);
        processedIndices.push(j);
        
        // Stop if we have enough content (2-4 sentences)
        if (summaryLines.join(' ').split(/[.!?]/).length >= 3) break;
      }
      
      if (summaryLines.length > 0) {
        processedIndices.push(i);
        return { summary: summaryLines.join(' ').trim(), processedIndices };
      }
    }
  }
  
  // Look for first substantial paragraph (2+ sentences, not a list item)
  for (let i = 0; i < lines.length && i < 3; i++) {
    const line = lines[i];
    if (line.length > 50 && 
        !line.match(/^[\d•\-*]/) && 
        !line.endsWith(':') &&
        !line.match(/^#{1,6}\s/)) {
      // Check if it contains 2+ sentences
      const sentences = line.split(/[.!?]/).filter(s => s.trim().length > 10);
      if (sentences.length >= 2) {
        processedIndices.push(i);
        return { summary: line, processedIndices };
      }
    }
  }
  
  return { summary: null, processedIndices: [] };
}

/**
 * Extract TrustScore if mentioned in the text
 * Looks for patterns like "TrustScore: 94", "Score: 94/100", etc.
 */
function extractTrustScore(text: string): number | null {
  const patterns = [
    /trust\s*score[:\s]+(\d+)(?:\/100)?/i,
    /score[:\s]+(\d+)(?:\/100)?/i,
    /rating[:\s]+(\d+)(?:\/100)?/i,
    /⭐\s*(\d+)(?:\/100)?/,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const score = parseInt(match[1], 10);
      if (score >= 0 && score <= 100) {
        return score;
      }
    }
  }
  
  return null;
}

/**
 * Extract cards from numbered lists with URLs and descriptions
 * Also handles bullet lists that look like options
 */
function extractCards(lines: string[]): any[] {
  const cards: any[] = [];
  const urlPattern = /(https?:\/\/[^\s]+)/gi;
  
  // Look for numbered list items (1. 2. 3.) or bullet points (•, -, *)
  const numberedPattern = /^(\d+)\.\s*(.+)/;
  const bulletPattern = /^[•\-*]\s+(.+)/;
  
  // Pre-compile icon detection patterns for performance
  const iconPatterns = [
    { regex: /comparador|compare|search|buscar|encuentra/i, icon: '🔍' },
    { regex: /rent|alquil|car|coche|vehicle|vehículo/i, icon: '🚗' },
    { regex: /web|online|internet|digital|sitio/i, icon: '🌐' },
    { regex: /local|oficina|office|tienda|store/i, icon: '🏢' },
    { regex: /hotel|hosped|accommodation|alojamiento/i, icon: '🏨' },
    { regex: /restaurant|comida|food|dining/i, icon: '🍽️' },
    { regex: /info|información|help|ayuda|guide|guía/i, icon: 'ℹ️' },
    { regex: /precio|price|cost|costo|tarifa/i, icon: '💰' },
  ];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let match = line.match(numberedPattern);
    let isBullet = false;
    let isNumbered = false;
    
    // If it's a numbered item
    if (match) {
      isNumbered = true;
    } else {
      // Check for bullet pattern
      const bulletMatch = line.match(bulletPattern);
      if (bulletMatch) {
        match = [line, '1', bulletMatch[1]]; // Fake match format
        isBullet = true;
      }
    }
    
    if (match) {
      const content = match[2];
      const urls = content.match(urlPattern);
      
      // Extract title (text before URL or dash)
      let title = content;
      let description = '';
      let link = '';
      
      if (urls && urls.length > 0) {
        link = urls[0];
        title = content.substring(0, content.indexOf(link)).trim();
        // Clean up common separators
        title = title.replace(/[-–—:]\s*$/, '').trim();
      }
      
      // Check if next line is a sub-description
      // For numbered lists: accept dash/bullet on next line
      // For bullet lists: only accept indented sub-items
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        let isSubItem = false;
        
        if (isNumbered) {
          // For numbered lists, next line starting with dash/bullet is a description
          isSubItem = !!nextLine.match(/^[-•*]\s*(.+)/) && !nextLine.match(/^\d+\./);
        } else if (isBullet) {
          // For bullet lists, only indented items are descriptions
          isSubItem = !!nextLine.match(/^[\s]{2,}[-•*]?\s*(.+)/);
        }
        
        if (isSubItem) {
          description = nextLine.replace(/^[\s]*[-•*]?\s*/, '').trim();
          i++; // Skip next line as it's been processed
        }
      }
      
      // Skip if title is too short (likely not a real card item)
      if (title && title.length > 2) {
        const card: any = { title };
        if (description) card.description = description;
        if (link) card.link = link;
        
        // Add icon based on content keywords using pre-compiled patterns
        const combinedText = (title + ' ' + (description || '')).toLowerCase();
        for (const { regex, icon } of iconPatterns) {
          if (regex.test(combinedText)) {
            card.icon = icon;
            break; // Use first matching icon
          }
        }
        
        cards.push(card);
      }
    }
  }
  
  return cards;
}

/**
 * Extract sections from text with headings
 * Detects: lines ending with ":", markdown headers (##), bold headers (**text**),
 * known section titles, ALL-CAPS headings, and numbered headings (e.g., "3. CONSEJOS")
 */
function extractSections(lines: string[]): any[] {
  const sections: any[] = [];
  let currentSection: any = null;
  
  for (const line of lines) {
    // Check for various heading patterns
    const isColonHeading = line.endsWith(':') && line.length < 100 && !line.match(/^https?:/);
    const isMarkdownHeading = line.match(/^#+\s+(.+)/);
    const isBoldHeading = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    // Check for known section titles
    const isKnownSection = SECTION_TITLE_PATTERNS.some(pattern => pattern.test(line));
    // Check for numbered sections like "3. CONSEJOS RÁPIDOS"
    const isNumberedSection = line.match(/^\d+\.\s+(.+)$/);
    // Check for ALL-CAPS headings (must contain letters and be reasonably short)
    const isCapitalizedShort = line.length < 60 && 
                               line.length > 3 &&
                               /[A-Z]/.test(line) && 
                               line === line.toUpperCase() && 
                               line.split(' ').length <= 5 &&
                               !line.match(/^https?:/i) &&
                               !line.match(/^\d+\.\s*https?:\/\//i); // Not a numbered URL
    
    // Filter out conversational text
    if (isConversationalText(line)) {
      continue;
    }
    
    if (isColonHeading || isMarkdownHeading || isBoldHeading || isCapitalizedShort || isKnownSection || isNumberedSection) {
      // Save previous section if exists and has content
      if (currentSection && currentSection.content.trim()) {
        sections.push(currentSection);
      }
      
      // Extract title based on pattern
      let title = line;
      if (isColonHeading) {
        title = line.replace(/:$/, '').trim();
      } else if (isMarkdownHeading) {
        title = isMarkdownHeading[1].trim();
      } else if (isBoldHeading) {
        title = isBoldHeading[1].trim();
      } else if (isNumberedSection) {
        title = isNumberedSection[1].trim();
      }
      
      // Add icon based on known patterns
      let icon = '';
      if (/^(main areas|zonas|areas)/i.test(title)) {
        icon = '📍';
      } else if (/^(what to do|qué hacer|actividades)/i.test(title)) {
        icon = '🏖️';
      } else if (/^(where to stay|alojarse|hospedaje)/i.test(title)) {
        icon = '🏨';
      } else if (/^(food|comida|restaurante)/i.test(title)) {
        icon = '🍽️';
      } else if (/^(beach|playa)/i.test(title)) {
        icon = '🏖️';
      } else if (/^(tip|consejo)/i.test(title)) {
        icon = '💡';
      } else if (/^(transport|transporte)/i.test(title)) {
        icon = '🚗';
      } else if (/^(culture|cultura)/i.test(title)) {
        icon = '🎭';
      } else if (/^(shopping|compra)/i.test(title)) {
        icon = '🛍️';
      }
      
      currentSection = {
        title: title,
        content: '',
        icon: icon || undefined,
      };
    } else if (currentSection) {
      // Add to current section content - capture ALL content until next section
      currentSection.content += (currentSection.content ? '\n' : '') + line;
    }
  }
  
  // Don't forget the last section
  if (currentSection && currentSection.content.trim()) {
    sections.push(currentSection);
  }
  
  // Process sections to extract URLs as cards and filter empty content
  return sections
    .filter(section => {
      // Filter out sections with empty or "---" content
      const trimmedContent = section.content.trim();
      return trimmedContent && trimmedContent !== '---' && trimmedContent !== '-';
    })
    .map(section => {
      // Check if section content contains URLs
      const urlCards = extractUrlsAsCards(section.content);
      
      if (urlCards.length > 0) {
        // If URLs found, use them as cards
        return {
          ...section,
          cards: urlCards,
        };
      }
      
      // Otherwise, keep the content as is
      return section;
    });
}

/**
 * Extract tips from recommendation patterns
 * Handles emojis, keywords, and bold text patterns
 * Maps to TipBox types: 'info', 'warning', 'success', 'tip'
 */
function extractTips(lines: string[]): any[] {
  const tips: any[] = [];
  
  for (const line of lines) {
    const isTip = TIP_PATTERNS.some(pattern => pattern.test(line));
    
    if (isTip) {
      let message = line;
      let variant: 'info' | 'success' | 'warning' | 'tip' = 'info';
      
      // Clean up prefix (emojis and keywords) - make colon optional
      message = message.replace(/^(tip|consejo|recomendación|importante|nota|note|important|aviso|advertencia):?\s*/i, '');
      message = message.replace(/^[💡ℹ️⚠️✅❗⭐🌟]\s*/, '');
      message = message.replace(/^\*\*(tip|consejo|recomendación|importante|nota|note|important):?\*\*\s*/i, '');
      
      // Determine variant based on content and emojis
      if (line.match(/[⚠️❗]/) || /importante|cuidado|atención|warning|caution|advertencia|aviso/i.test(line)) {
        variant = 'warning';
      } else if (line.match(/[✅⭐]/) || /recomendado|mejor|excelente|best|recommended|success/i.test(line)) {
        variant = 'success';
      } else if (line.match(/[💡🌟]/) || /tip|consejo|recomendación/i.test(line)) {
        variant = 'tip';
      }
      
      // Only add if message is not empty after cleanup
      if (message.trim()) {
        tips.push({
          message: message.trim(),
          variant,
        });
      }
    }
  }
  
  return tips;
}

/**
 * Extract cards from **bold title** – description format
 * Example: "**Marbella, Spain** – the coastal city on the Costa del Sol"
 */
function extractBoldTitleCards(text: string): any[] | null {
  const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
  
  // Pattern to match **title** – description or **title**: description
  // Using proper character class with hyphen at the end to avoid confusion
  const boldTitlePattern = /^\*\*(.+?)\*\*\s*[–—:-]\s*(.+)/;
  
  const cards: any[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(boldTitlePattern);
    
    if (match) {
      const title = match[1].trim();
      const description = match[2].trim();
      
      // Generate automatic TrustScore based on position
      const trustScore = generateTrustScore(cards.length);
      
      // Extract tags from title and description
      const tags = extractTags(title + ' ' + description);
      
      // Try to extract or generate URL
      const url = extractOrGenerateUrl(title, description);
      
      cards.push({
        title,
        description,
        trustScore,
        tags,
        url,
      });
    }
  }
  
  // Only return if we found at least 2 cards (likely a list of results)
  return cards.length >= 2 ? cards : null;
}

/**
 * Generate automatic TrustScore based on result position
 * - Result 1: 94-98
 * - Result 2: 88-92
 * - Result 3: 82-88
 * - Result 4+: 70-82
 * 
 * Note: Uses randomization for variability. For deterministic testing,
 * consider passing a seed or making randomization optional.
 */
function generateTrustScore(position: number): number {
  switch (position) {
    case 0:
      return 94 + Math.floor(Math.random() * 5); // 94-98
    case 1:
      return 88 + Math.floor(Math.random() * 5); // 88-92
    case 2:
      return 82 + Math.floor(Math.random() * 7); // 82-88
    default:
      return 70 + Math.floor(Math.random() * 13); // 70-82
  }
}

/**
 * Extract tags based on keywords in content
 */
function extractTags(content: string): Tag[] {
  const tags: Tag[] = [];
  const lowerContent = content.toLowerCase();
  
  // Default colors for tags - matching TagChips component
  const defaultColors = [
    '#dbeafe', // blue
    '#dcfce7', // green
    '#fef3c7', // yellow
    '#fce7f3', // pink
    '#e0e7ff', // indigo
    '#f3e8ff', // purple
  ];
  
  // Define tag patterns with their icons, labels, and colors
  const tagPatterns = [
    { keywords: ['travel', 'viaje', 'turismo', 'tourism'], icon: '🌍', label: 'Travel', color: '#dbeafe' },
    { keywords: ['hotel', 'accommodation', 'alojamiento', 'hospedaje'], icon: '🏨', label: 'Hotel', color: '#fce7f3' },
    { keywords: ['restaurant', 'food', 'comida', 'dining', 'gastronomía'], icon: '🍽️', label: 'Food', color: '#dcfce7' },
    { keywords: ['beach', 'playa', 'costa', 'coast'], icon: '🏖️', label: 'Beach', color: '#dbeafe' },
    { keywords: ['nightlife', 'club', 'bar', 'disco', 'night'], icon: '🎉', label: 'Nightlife', color: '#e0e7ff' },
    { keywords: ['culture', 'cultura', 'museum', 'museo', 'art', 'arte'], icon: '🎭', label: 'Culture', color: '#f3e8ff' },
    { keywords: ['shopping', 'compras', 'tienda', 'store', 'mall'], icon: '🛍️', label: 'Shopping', color: '#fef3c7' },
    { keywords: ['nature', 'naturaleza', 'park', 'parque', 'hiking'], icon: '🌲', label: 'Nature', color: '#dcfce7' },
    { keywords: ['sport', 'deporte', 'activity', 'actividad'], icon: '⚽', label: 'Sports', color: '#dbeafe' },
    { keywords: ['real estate', 'property', 'propiedad', 'inmobiliaria'], icon: '🏠', label: 'Real Estate', color: '#fce7f3' },
    { keywords: ['information', 'información', 'info', 'guide', 'guía'], icon: 'ℹ️', label: 'Info', color: '#e0e7ff' },
  ];
  
  // Check each pattern
  for (const pattern of tagPatterns) {
    if (pattern.keywords.some(keyword => lowerContent.includes(keyword))) {
      tags.push({
        label: pattern.label,
        icon: pattern.icon,
        color: pattern.color,
      });
      
      // Limit to 3 tags
      if (tags.length >= 3) break;
    }
  }
  
  return tags;
}

/**
 * Extract URL from content or generate Google search fallback
 */
function extractOrGenerateUrl(title: string, description: string): string {
  const content = title + ' ' + description;
  
  // Try to extract URL
  const urlPattern = /(https?:\/\/[^\s]+)/;
  const match = content.match(urlPattern);
  
  if (match) {
    return match[1];
  }
  
  // Generate Google search URL as fallback
  const searchQuery = encodeURIComponent(title);
  return `https://www.google.com/search?q=${searchQuery}`;
}

/**
 * Generate automatic TrustScore for a card based on position
 * Exported for use in ChatResponse.tsx
 */
export function generateAutoTrustScore(position: number): number {
  return generateTrustScore(position);
}

/**
 * Extract automatic tags from content
 * Exported for use in ChatResponse.tsx
 */
export function extractAutoTags(content: string): Tag[] {
  return extractTags(content);
}

/**
 * Extract URLs from content and convert them to cards
 * Each URL becomes a separate card with extracted domain info
 */
export function extractUrlsAsCards(content: string): any[] {
  const cards: any[] = [];
  const lines = content.split('\n').map(line => line.trim()).filter(Boolean);
  const urlPattern = /(https?:\/\/[^\s]+)/gi;
  
  for (const line of lines) {
    const urls = line.match(urlPattern);
    
    if (urls && urls.length > 0) {
      for (const url of urls) {
        // Extract domain from URL
        try {
          const urlObj = new URL(url);
          const domain = urlObj.hostname.replace(/^www\./, '');
          
          // Extract text before URL as potential title
          const textBeforeUrl = line.substring(0, line.indexOf(url)).trim();
          const cleanText = textBeforeUrl.replace(/^[\-•*\d.)\s]+/, '').trim();
          
          const domainPart = domain.split('.')[0];
          const title = cleanText || domainPart.charAt(0).toUpperCase() + domainPart.slice(1);
          
          // Generate trust score
          const trustScore = generateTrustScore(cards.length);
          
          // Extract tags based on domain
          const domainTags = extractTagsFromDomain(domain);
          
          cards.push({
            title,
            description: `Visit ${domain} for more information`,
            url,
            trustScore,
            tags: domainTags,
            icon: getIconForDomain(domain),
          });
        } catch (e) {
          // If URL parsing fails, skip this URL
          continue;
        }
      }
    }
  }
  
  return cards;
}

/**
 * Extract tags based on domain name
 */
function extractTagsFromDomain(domain: string): Tag[] {
  const tags: Tag[] = [];
  const lowerDomain = domain.toLowerCase();
  
  // Ferry/transport domains
  if (lowerDomain.includes('ferry') || lowerDomain.includes('naviera') || lowerDomain.includes('armas') || lowerDomain.includes('fredolsen')) {
    tags.push({ label: 'Ferry', icon: '⛴️', color: '#dbeafe' });
    tags.push({ label: 'Transport', icon: '🚢', color: '#e0e7ff' });
  }
  
  // Hotel/accommodation domains
  if (lowerDomain.includes('hotel') || lowerDomain.includes('booking') || lowerDomain.includes('airbnb')) {
    tags.push({ label: 'Hotel', icon: '🏨', color: '#fce7f3' });
  }
  
  // Travel/tourism domains
  if (lowerDomain.includes('travel') || lowerDomain.includes('tour') || lowerDomain.includes('viaje')) {
    tags.push({ label: 'Travel', icon: '✈️', color: '#dbeafe' });
  }
  
  // Restaurant/food domains
  if (lowerDomain.includes('restaurant') || lowerDomain.includes('food') || lowerDomain.includes('dining')) {
    tags.push({ label: 'Food', icon: '🍽️', color: '#dcfce7' });
  }
  
  // Information/guide domains
  if (lowerDomain.includes('info') || lowerDomain.includes('guide') || lowerDomain.includes('wiki')) {
    tags.push({ label: 'Info', icon: 'ℹ️', color: '#e0e7ff' });
  }
  
  // If no specific tags found, add a generic web tag
  if (tags.length === 0) {
    tags.push({ label: 'Web', icon: '🌐', color: '#f3e8ff' });
  }
  
  return tags.slice(0, 3); // Limit to 3 tags
}

/**
 * Get icon for domain
 */
function getIconForDomain(domain: string): string {
  const lowerDomain = domain.toLowerCase();
  
  if (lowerDomain.includes('ferry') || lowerDomain.includes('naviera') || lowerDomain.includes('armas')) {
    return '⛴️';
  }
  if (lowerDomain.includes('fredolsen')) {
    return '🚢';
  }
  if (lowerDomain.includes('hotel') || lowerDomain.includes('booking')) {
    return '🏨';
  }
  if (lowerDomain.includes('restaurant') || lowerDomain.includes('food')) {
    return '🍽️';
  }
  if (lowerDomain.includes('info') || lowerDomain.includes('guide')) {
    return 'ℹ️';
  }
  
  return '🌐'; // Default web icon
}

/**
 * Check if content is conversational text that should be filtered
 */
export function isConversationalText(text: string): boolean {
  return CONVERSATIONAL_PATTERNS.some(pattern => pattern.test(text.trim()));
}

